import React from 'react';
import TikTokFeedItem, { displayName } from './TikTokFeedItem';

/**
 * The TikTok chat frame: header stats and toggles, disconnect alert, feed (or empty state) and the
 * join ticker. Purely presentational; TikTokChat owns the connection, state and timers.
 *
 * connectionStatus  'connecting' | 'connected' | 'offline' (empty-state text)
 * tickerEnabled     joins show one at a time in the ticker instead of the feed
 * chatOnlyFilter    the feed shows chat messages only
 */
const TikTokChatView = ({
    username,
    active,
    messages,
    connectionStatus,
    disconnectError,
    viewerCount,
    totalLikeCount,
    tickerEnabled,
    chatOnlyFilter,
    currentTickerMessage,
    hasNewMessages,
    scrollContainerRef,
    messagesEndRef,
    onScroll,
    onToggleTicker,
    onToggleChatOnly,
    onReconnect,
    onScrollToBottom,
}) => {
    if (!username) {
        return (
            <div className={`chat-container tiktok ${active ? 'active' : ''}`}>
                <div className="chat-banner">
                    <p>No TikTok Channel</p>
                </div>
                <div className="tiktokframe">
                    <div className="tiktok-chat-header">Chat</div>
                    <div className="no-channel">No channel selected</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`chat-container tiktok ${active ? 'active' : ''}`} id={username}>
            <div className="chat-banner">
                <p>{username}</p>
            </div>

            <div className="tiktokframe">
                <div className="tiktok-chat-header">
                    <span>Chat</span>
                    <div className="tiktok-stats">
                        {/* <span className={`status-indicator ${isConnected ? 'live' : 'offline'}`}>
                            {isConnected ? '🔴' : '⚫'}
                        </span> */}
                        {totalLikeCount > 0 && (
                            <span className="like-count">👍 {totalLikeCount.toLocaleString()}</span>
                        )}
                        {viewerCount > 0 && (
                            <span className="viewer-count">👁️ {viewerCount.toLocaleString()}</span>
                        )}
                        <button
                            className={`ticker-toggle ${tickerEnabled ? 'active' : ''}`}
                            onClick={onToggleTicker}
                            title={tickerEnabled ? 'Disable Join Ticker' : 'Enable Join Ticker'}
                        >
                            {tickerEnabled ? '👋 ticker' : '👋 feed'}
                        </button>
                        <button
                            className={`chat-only-toggle ${chatOnlyFilter ? 'active' : ''}`}
                            onClick={onToggleChatOnly}
                            title={chatOnlyFilter ? 'Show All Messages' : 'Show Only Chat Messages'}
                        >
                            {chatOnlyFilter ? '💬 only' : '💬 all'}
                        </button>
                    </div>
                </div>

                {disconnectError && (
                    <div className="tiktok-disconnect-alert">
                        <span className="alert-message">⚠️ {disconnectError}</span>
                        <button className="alert-reconnect-btn" onClick={onReconnect}>
                            Reconnect
                        </button>
                    </div>
                )}

                <div
                    className="tiktok-messages-container"
                    ref={scrollContainerRef}
                    onScroll={onScroll}
                >
                    {messages.length === 0 ? (
                        <div className="empty-state">
                            {connectionStatus === 'offline'
                                ? 'Channel is offline'
                                : connectionStatus === 'connected'
                                ? 'Waiting for messages...'
                                : 'Connecting...'}
                        </div>
                    ) : (
                        <>
                            {messages
                                .filter(msg => {
                                    // Filter out member messages if ticker is enabled
                                    if (tickerEnabled && msg.type === 'member') return false;
                                    // Filter to only chat messages if chat only filter is enabled
                                    if (chatOnlyFilter && msg.type !== 'chat') return false;
                                    return true;
                                })
                                .map(msg => <TikTokFeedItem key={msg.id} msg={msg} />)
                            }
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {hasNewMessages && (
                    <button className="new-messages-indicator" onClick={onScrollToBottom}>
                        ↓ New messages below
                    </button>
                )}

                {tickerEnabled && (
                    <div key="ticker-display" className="tiktok-ticker-container">
                        {currentTickerMessage && (
                            <div className="tiktok-ticker-message">
                                <span className="ticker-icon">👋</span>
                                <span className="ticker-text">
                                    <strong>{displayName(currentTickerMessage.user)}</strong> joined!
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TikTokChatView;
